/* @flow */
import React from 'react';
import { observer } from 'mobx-react';
import { observable } from 'mobx';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Typography from '@material-ui/core/Typography';
import Collapse from '@material-ui/core/Collapse';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import { ComponentWithInjectedProps } from 'utils/injectedProps';
import Loading from 'components/Loading/Loading';
import TitleActionButtonRow from 'components/TitleActionButtonRow';
import type { ActionButtonSpec } from 'components/TitleActionButtonRow';

import styles from './ReportSummary.module.scss';

export type SummaryItem = {
  label: string,
} & (
  | {
      data: (string | number)[],
    }
  | {
      isSubHeading: true,
    }
);

export type SummaryBlock = {
  title: string,
  columns: { heading: string, align?: 'left' | 'center' | 'right' }[],
  data: SummaryItem[],
};

/**
 * Block layout: [column][row]
 */
export type SummaryData = SummaryBlock[][];

export type ReportSummaryProps = {
  title: string,
  data: SummaryData,
  buttons?: ActionButtonSpec[],
  buttonsLoading?: boolean,
  loading?: boolean,
  onExportDataClick: () => void,
  expanded?: boolean,
  onHeaderClick?: () => void,
};

type InjectedProps = TranslatorProps;

@translate()
@observer
export default class ReportSummary extends ComponentWithInjectedProps<
  ReportSummaryProps,
  {},
  InjectedProps
> {
  handleSummaryCardHeaderClick = (event: React.SyntheticEvent<HTMLDivElement>) => {
    const { onHeaderClick } = this.props;

    if (onHeaderClick) {
      onHeaderClick();
    }
  };

  renderSummaryTable = (block: SummaryBlock, blockIndex: number, isLast?: boolean) => {
    const { t } = this.props;

    return (
      <Paper
        key={typeof blockIndex === 'number' ? `summary-block-${blockIndex}` : undefined}
        className={`${styles.tablePaper} ${isLast ? styles.last : ''}`}
      >
        <Table className={styles.table} padding="dense">
          <TableHead>
            <TableRow classes={{ root: styles.headerRow }}>
              <TableCell classes={{ root: `${styles.cell} ${styles.titleCell}` }}>
                {t(block.title)}
              </TableCell>

              {block.columns.map((column, index) => (
                <TableCell
                  key={`summary-block-column-${index}`}
                  classes={{
                    root: `${styles.cell} ${styles.headCell} ${
                      styles[`align-${column.align || 'center'}`]
                    }`,
                  }}
                >
                  {t(column.heading)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {block.data.map((row, rowIndex) => (
              <TableRow key={`summary-block-row-${rowIndex}`} classes={{ root: styles.bodyRow }}>
                <TableCell
                  classes={{
                    root: `${styles.cell} ${styles.labelCell} ${
                      row.isSubHeading ? styles.subHeadingCell : ''
                    }`,
                  }}
                >
                  {t(row.label)}
                </TableCell>

                {!row.isSubHeading &&
                  row.data &&
                  row.data.map((cell, index) => (
                    <TableCell
                      key={`summary-block-cell-${index}`}
                      classes={{
                        root: `${styles.cell} ${
                          styles[`align-${block.columns[index].align || 'center'}`]
                        }`,
                      }}
                    >
                      {cell}
                    </TableCell>
                  ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    );
  };

  renderSummaryContent = () => {
    const { data, loading } = this.props;

    if (loading) {
      return (
        <div className={styles.loadingWrapper}>
          <Loading />
        </div>
      );
    }

    if (data) {
      return (
        <div className={styles.tablesWrapper}>
          {data.map((sectionColumn, index) => (
            <div key={`summary-section-column-${index}`} className={styles.tablesColumn}>
              {sectionColumn.map((block, blockIndex) =>
                this.renderSummaryTable(block, blockIndex, blockIndex === sectionColumn.length - 1)
              )}
            </div>
          ))}
        </div>
      );
    }
  };

  render() {
    const { t, expanded, title, buttons, buttonsLoading } = this.props;

    return (
      <Card className={`${styles.reportSummaryComponent} ${styles.summary}`}>
        <CardHeader
          className={styles.cardHeaderRoot}
          onClick={this.handleSummaryCardHeaderClick}
          title={
            <div className={styles.cardHeaderWrapper}>
              <svg className={`${styles.collapseIcon} ${this.expanded ? styles.expanded : ''}`}>
                <use xlinkHref={'#icon-chevron-down'} />
              </svg>

              <Typography variant="headline">{t(title)}</Typography>

              <div className={styles.spacer} />

              {buttons && (
                <div className={styles.headerButtons} onClick={e => e.stopPropagation()}>
                  <TitleActionButtonRow buttons={buttons} loading={buttonsLoading} />
                </div>
              )}
            </div>
          }
        />
        <Collapse in={expanded} classes={{ container: styles.collapseContainer }}>
          <CardContent className={styles.content}>{this.renderSummaryContent()}</CardContent>
        </Collapse>
      </Card>
    );
  }
}
